export const nodeTypesConfig = {     
    start: {        
      label: 'Start',
      description: 'Starting point of your flow',
      inputs: [],
      outputs: ['output'],
      // Common node settings
      settings: {
        globalNode: false,
        skipResponse: false,
        blockInterruptions: false
      }
    },
    chatCompletion: {
      label: 'Chat Completion',
      description: 'Generate AI responses using LLMs',
      inputs: [
        { 
          id: 'provider', 
          type: 'select', 
          label: 'Provider', 
          options: ['OpenAI', 'Anthropic', 'Cohere', 'Grok', 'Mixtral'],
          default: 'OpenAI'
        },
        { 
          id: 'llm', 
          type: 'dynamicSelect', 
          label: 'Model',
          dependsOn: 'provider',
          options: {
            'OpenAI': ['GPT-4o', 'GPT-4', 'GPT-3.5-turbo', 'GPT-4o-mini'],
            'Anthropic': ['Claude 3 Opus', 'Claude 3 Sonnet', 'Claude 3 Haiku'],
            'Cohere': ['Command', 'Command Light'],
            'Grok': ['Grok-1', 'Grok-1 Mini'],
            'Mixtral': ['Mixtral 8x7B', 'Mixtral 8x22B']
          },
          default: 'GPT-4o'
        },
        { 
          id: 'prompt', 
          type: 'textarea', 
          label: 'System Prompt',
          placeholder: 'Instructions for the AI assistant...'
        },
        { 
          id: 'temperature', 
          type: 'slider', 
          label: 'Temperature', 
          min: 0, 
          max: 1, 
          step: 0.1, 
          default: 0.7,
          description: 'Controls randomness: 0 is deterministic, 1 is creative'
        },
        { 
          id: 'maxTokens', 
          type: 'number', 
          label: 'Max Response Length', 
          min: 50, 
          max: 4096,
          default: 1024,
          description: 'Maximum number of tokens in the response'
        },
        { 
          id: 'apiKey', 
          type: 'text', 
          label: 'API Key',
          isSecret: true
        },
        { 
          id: 'tools', 
          type: 'toolList', 
          label: 'Function Tools', 
          default: [] 
        },
        {
          id: 'examples',
          type: 'examplesList',
          label: 'Fine-tuning Examples',
          description: 'Add example conversations to guide model behavior',
          default: []
        }
      ],
      outputs: ['response'],
      // Extended settings for chat completion
      settings: {
        globalNode: false,
        skipResponse: false,
        blockInterruptions: true,
        streamResponse: true,
        responseFormat: 'chat',
        fallbackBehavior: 'retry',
        optimizationTarget: 'quality',
        spawnToolNodes: true // New flag to indicate tool nodes should be spawned
      }
    },
    apiRequest: {
      label: 'API Request',
      description: 'Make HTTP requests to external APIs',
      inputs: [
        {
          id: 'requestConfig',
          label: 'Request',
          type: 'custom',
          default: { method: 'GET', url: '' }
        },
        {
          id: 'headers',
          label: 'Headers',
          type: 'custom',
          default: []
        },
        {
          id: 'body',
          label: 'Request Body',
          type: 'textarea',
          placeholder: 'Enter request body (for POST, PUT, PATCH)'
        },
        {
          id: 'parseResponse',
          label: 'Parse Response as JSON',
          type: 'select',
          options: ['Yes', 'No'],
          default: 'Yes'
        },
        {
          id: 'timeout',
          label: 'Timeout (ms)',
          type: 'number',
          default: 10000,
          min: 1000,
          max: 60000
        },
        {
          id: 'retries',
          label: 'Retries',
          type: 'number',
          default: 0,
          min: 0,
          max: 5
        }
      ],
      outputs: ['success', 'error'],
      settings: {
        globalNode: false,
        skipResponse: false,
        cacheResults: false,
        errorHandling: 'continue' // continue, stop, fallback
      }
    },
    storeVariable: {
      label: 'Store Variable',
      description: 'Store values for use elsewhere in the flow',
      inputs: [
        {
          id: 'variableName',
          label: 'Variable Name',
          type: 'text',
          placeholder: 'Enter variable name (e.g., user.name)'
        },
        {
          id: 'variableSource',
          label: 'Source',
          type: 'custom',
          default: { type: 'Last Message', value: '' }
        },
        {
          id: 'scope',
          label: 'Variable Scope',
          type: 'select',
          options: ['Conversation', 'Session', 'User', 'Global'],
          default: 'Conversation',
          description: 'How long the variable will persist'
        }
      ],
      outputs: ['default'],
      settings: {
        globalNode: false,
        skipResponse: true,
        overwriteExisting: true
      }
    },
    findAndReplace: {
      label: 'Find and Replace',
      description: 'Search and replace text in messages',
      inputs: [
        { id: 'find', type: 'text', label: 'Find' },
        { id: 'replace', type: 'text', label: 'Replace' },
        { 
          id: 'mode', 
          type: 'select', 
          label: 'Match Mode', 
          options: ['Simple', 'Regex', 'Case Sensitive'],
          default: 'Simple'
        }
      ],
      outputs: ['output'],
      settings: {
        globalNode: false,
        skipResponse: false,
        replaceAll: true
      }
    },
    knowledgeBase: {
      label: 'Knowledge Base',
      description: 'Add context from Pinecone vector database',
      inputs: [
        {
          id: 'openaiApiKey',
          label: 'OpenAI API Key',
          type: 'text',
          isSecret: true
        },
        {
          id: 'pineconeApiKey',
          label: 'Pinecone API Key',
          type: 'text',
          isSecret: true
        },
        {
          id: 'pineconeEnvironment',
          label: 'Pinecone Environment',
          type: 'text',
          placeholder: 'e.g., us-west1-gcp'
        },
        {
          id: 'pineconeIndex',
          label: 'Pinecone Index Name',
          type: 'text'
        },
        {
          id: 'pineconeNamespace',
          label: 'Pinecone Namespace (Optional)',
          type: 'text'
        },
        {
          id: 'topK',
          label: 'Number of Results',
          type: 'number',
          default: '3',
          min: 1,
          max: 10
        },
        {
          id: 'similarityThreshold',
          label: 'Similarity Threshold',
          type: 'number',
          default: '0.7',
          min: 0,
          max: 1,
          step: 0.01
        },
        {
          id: 'model',
          label: 'Embedding Model',
          type: 'select',
          options: ['text-embedding-3-small', 'text-embedding-3-large', 'text-embedding-ada-002'],
          default: 'text-embedding-3-small'
        },
        {
          id: 'includeMetadata',
          label: 'Include Source Metadata',
          type: 'select',
          options: ['Yes', 'No'],
          default: 'Yes'
        }
      ],
      outputs: ['default'],
      settings: {
        globalNode: false,
        skipResponse: false,
        cacheResults: true,
        maxCacheAge: 3600 // in seconds
      }
    },
    ifElse: {
      label: 'If/Else',
      description: 'Create conditional branches',
      inputs: [
        { 
          id: 'operator', 
          type: 'select', 
          label: 'Operator', 
          options: ['equals', 'does not equal', 'is greater than', 'is less than', 'includes', 'is empty', 'is not empty', 'matches regex']
        },
        { id: 'value', type: 'text', label: 'Value' },
      ],
      outputs: ['true', 'false'],
      settings: {
        globalNode: false,
        skipResponse: true,
        evaluateOnce: true
      }
    },
    aiDecision: {
      label: 'AI Decision',
      description: 'Let AI classify and route messages',
      inputs: [
        { id: 'apiKey', type: 'text', label: 'API Key', isSecret: true },
        { 
          id: 'model', 
          type: 'select', 
          label: 'Model',
          options: ['GPT-4', 'GPT-3.5-turbo', 'GPT-4o'],
          default: 'GPT-4o'
        },
        { 
          id: 'instruction', 
          type: 'textarea', 
          label: 'Decision Instructions',
          placeholder: 'Explain how the AI should classify messages...'
        },
        { id: 'outcomes', type: 'custom', label: 'Outcomes', default: [{ condition: 'Fallback', id: 'fallback' }] },
      ],
      outputs: [], // Dynamically set by outcomes
      settings: {
        globalNode: false,
        skipResponse: false,
        confidence: 0.7, // Minimum confidence threshold
        fallbackBehavior: 'default' // default, retry, ask
      }
    },
    userInput: {
      label: 'User Input',
      description: 'Collect input from the user',
      inputs: [
        {
          id: 'prompt',
          label: 'Prompt Message',
          type: 'textarea',
          placeholder: 'Message to display before user input'
        },
        {
          id: 'inputType',
          label: 'Input Type',
          type: 'select',
          options: ['text', 'number', 'email', 'options'],
          default: 'text'
        },
        {
          id: 'options',
          label: 'Options',
          type: 'custom',
          placeholder: 'Only for options input type',
          showIf: { inputType: 'options' },
          default: []
        },
        {
          id: 'validation',
          label: 'Validation',
          type: 'select',
          options: ['none', 'required', 'custom'],
          default: 'none'
        },
        {
          id: 'validationRule',
          label: 'Validation Rule',
          type: 'text',
          placeholder: 'Regex or validation function',
          showIf: { validation: 'custom' }
        }
      ],
      outputs: ['valid', 'invalid'],
      settings: {
        globalNode: false,
        skipResponse: false,
        timeoutSeconds: 120,
        timeoutBehavior: 'reminder' // reminder, skip, end
      }
    },
    output: {
      label: 'Output',
      description: 'Final response to the user',
      inputs: [
        {
          id: 'message',
          label: 'Message (Optional)',
          type: 'textarea',
          placeholder: 'Optional static message to append'
        },
        {
          id: 'formatOutput',
          label: 'Format Output',
          type: 'select',
          options: ['None', 'JSON', 'Markdown', 'HTML'],
          default: 'None'
        }
      ],
      outputs: [],
      settings: {
        globalNode: false,
        skipResponse: false,
        markConversationComplete: true
      }
    },
    sentiment: {
      label: 'Sentiment Analysis',
      description: 'Analyze message sentiment',
      inputs: [
        {
          id: 'provider',
          label: 'Provider',
          type: 'select',
          options: ['OpenAI', 'Built-in'],
          default: 'Built-in'
        },
        {
          id: 'apiKey',
          label: 'API Key',
          type: 'text',
          isSecret: true,
          showIf: { provider: 'OpenAI' }
        }
      ],
      outputs: ['positive', 'neutral', 'negative'],
      settings: {
        globalNode: false,
        skipResponse: true,
        detailedAnalysis: false
      }
    },
    entityExtraction: {
      label: 'Entity Extraction',
      description: 'Extract entities from text',
      inputs: [
        {
          id: 'entities',
          label: 'Entities to Extract',
          type: 'custom',
          default: []
        },
        {
          id: 'provider',
          label: 'Provider',
          type: 'select',
          options: ['OpenAI', 'Built-in'],
          default: 'OpenAI'
        },
        {
          id: 'apiKey',
          label: 'API Key',
          type: 'text',
          isSecret: true,
          showIf: { provider: 'OpenAI' }
        }
      ],
      outputs: ['success', 'failure'],
      settings: {
        globalNode: false,
        skipResponse: true,
        storeEntities: true,
        storePrefix: 'entity.'
      }
    },
  };